// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import Factory from "@store/Library/Components/Icons/Factory";
import { StatusProps } from "@store/Library/Components/Status";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends ExpandStyles<ProductShipsFromStyles> {
    pageType?: string;
    pricing?: ProductPriceDto | null;
    displayIcons?: boolean;
    displayTexts?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isProductPage: getCurrentPage(state).type === "ProductDetailsPage",
});

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductShipsFromStyles {
    loadingText?: TypographyPresentationProps;
    shipFromWrapper?: InjectableCss;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: TypographyPresentationProps;
    statusStyles?: StatusProps;
    ShipsFromMessage?: ShipsFromMessage;
    shipFromIcon?: TooltipPresentationProps;
}

interface ShipsFromMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const productShipsFromStyles: ProductShipsFromStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    shipFromWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            margin-bottom: ${pxToRem(8)};
        `,
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    shipFromIcon: {
        cssOverrides: {
            tooltipWrapper: css`
                ${x => x.theme.tooltip.defaultProps?.cssOverrides?.tooltipWrapper}
                margin-left: 0;
            `,
        },
        iconProps: {
            src: Factory,
            css: css`
                margin-left: 0;
            `,
        },
    },
};

const ProductShipsFrom = ({
    product,
    productInfo,
    extendedStyles,
    overridedStyles,
    pageType,
    pricing,
    displayIcons = false,
    displayTexts = true,
    isProductPage,
}: Props) => {
    const styles = useExpandProps(productShipsFromStyles, extendedStyles, overridedStyles);
    const displayIcon = isProductPage ? true : displayIcons;

    let warehouseDisplay: string | undefined;
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (warehouseDisplay) {
            setIsLoading(false);
        }
    }, [warehouseDisplay]);

    if (pageType === "OrderConfirmationPage") {
        return null;
    }

    if (pricing) {
        warehouseDisplay = pricing.additionalResults?.warehouseDescription;
    } else if (product && productInfo) {
        const { pricing } = productInfo;
        const defaultPlantDisplay = pricing?.additionalResults?.warehouseDescription;
        warehouseDisplay = defaultPlantDisplay;
    }

    return (
        <StyledWrapper {...styles.shipFromWrapper}>
            {displayIcon && (
                <Tooltip {...styles.shipFromIcon} text={siteMessage("ProductInfo_ShipFromIconMessage") as string} />
            )}
            <Typography {...styles.additionalInfoLabel}>
                {displayTexts && `${translate("Ships from")}${translate(":")}`}
            </Typography>
            {isLoading ? (
                <Typography {...styles.loadingText}>&nbsp;</Typography>
            ) : (
                <Typography {...styles.additionalInfoText}>{` ${warehouseDisplay}`}</Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductShipsFrom));
